import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { settingsApi, analyticsApi } from '../services/api';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await settingsApi.get();
      setSettings(response.data);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      setError('Failed to load settings');
    }
  }, []);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await analyticsApi.getDashboard();
      setDashboardData(response.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    }
  }, []);

  // Update settings
  const updateSettings = async (newSettings) => {
    try {
      const response = await settingsApi.update(newSettings);
      setSettings(response.data);
      showSnackbar('Settings updated successfully', 'success');
      return response.data;
    } catch (err) {
      showSnackbar('Failed to update settings', 'error');
      throw err;
    }
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchDashboardData()]);
      setLoading(false);
    };
    init();
  }, [fetchSettings, fetchDashboardData]);

  // Refresh dashboard data periodically
  useEffect(() => {
    const interval = setInterval(fetchDashboardData, 60000); // Every minute
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const value = {
    settings,
    dashboardData,
    loading,
    error,
    snackbar,
    sidebarOpen,
    fetchSettings,
    fetchDashboardData,
    updateSettings,
    showSnackbar,
    closeSnackbar,
    toggleSidebar,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContext;
